/* eslint-disable @typescript-eslint/member-ordering */
import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { faArrowLeft, faArrowRight } from '@fortawesome/pro-regular-svg-icons';
import { TranslateService } from '@ngx-translate/core';

import { AiChatPageState, SurveyResult } from '@empathy/survey/base';
import { PageModel } from '@empathy/survey-core';

import { SurveyNavigationContract } from '../../models/survey-navigation-contract.interface';
import { SurveyNavigationService } from '../../services/survey-navigation.service';
import { SurveyViewerStateService } from '../../services/survey-viewer-state.service';
import { SurveyViewerService } from '../../services/survey-viewer.service';

@Component({
  selector: 'empathy-page-view',
  templateUrl: 'page-view.component.html',
  styleUrls: ['./page-view.component.scss', './_page-view-theme.component.scss'],
  animations: [trigger('showFooter', [transition(':enter', [style({ opacity: 0 }), animate('250ms cubic-bezier(.17,.67,1,1.04)')])])]
})
export class PageViewComponent {
  private surveyNavigationService: SurveyNavigationContract = inject(SurveyNavigationService);
  private surveyViewerService = inject(SurveyViewerService);
  private surveyViewerStateService = inject(SurveyViewerStateService);

  page = input<PageModel>();
  chatState = input<AiChatPageState>({});
  resumeAnswers = input<SurveyResult>();
  completionPending = input<boolean>(false);
  previewMode = input(false);

  httpError = output();
  next = output();
  prev = output();

  activePage$ = signal<PageModel>(null);
  forceStop$ = signal(false);
  stopNextQuestionButton$ = computed(
    () =>
      this.surveyNavigationService.isSaving$() ||
      this.surveyNavigationService.isCompleting$() ||
      (!this.completionPending() && (this.surveyViewerService.notAllCtrlsValid$() || this.forceStop$() || !this.surveyViewerStateService.canProceed$()))
  );
  stopBackButton$ = computed(
    () => this.surveyNavigationService.isSaving$() || this.surveyNavigationService.isCompleting$() || this.surveyNavigationService.isGoingBack$()
  );

  readonly isCompleting$ = this.surveyNavigationService.isCompleting$;
  readonly isSaving$ = this.surveyNavigationService.isSaving$;
  readonly isGoingBack$ = this.surveyNavigationService.isGoingBack$;
  readonly nextButtonText$ = computed(() => {
    const remaining = this.surveyViewerStateService.remainingViewTime$();

    const text = this.completionPending() ? this.completeLabel$() : this.nextQuestionLabel$();
    return remaining > 0 ? `${text} (${remaining}s)` : text;
  });
  readonly notAllCtrlsValid$ = this.surveyViewerService.notAllCtrlsValid$;
  readonly progress$ = this.surveyViewerService.progress$;
  readonly questionControls$ = this.surveyViewerService.questionControls$;
  // Test mode overrides the survey setting, but never the absence of an earlier page to return to.
  readonly shouldShowBackButton$ = computed(
    () => this.surveyViewerService.canGoBackInd$() && (this.surveyViewerService.isTestMode$() || !this.surveyViewerService.isBackButtonDisabled$())
  );
  readonly visibleQuestions$ = this.surveyViewerService.visibleQuestions$;

  readonly faArrowLeft = faArrowLeft;
  readonly faArrowRight = faArrowRight;

  private currentPage: PageModel;
  private automaticSubmissionPageId: string;

  private translate = inject(TranslateService);
  private controls$ = computed(() => Object.values(this.surveyViewerService.questionControls$()));
  private completeLabel$ = toSignal(this.translate.stream('system.complete'), { initialValue: '' });
  private nextQuestionLabel$ = toSignal(this.translate.stream('system.nextQuestion'), { initialValue: '' });

  constructor() {
    effect(
      () => {
        const page = this.page();
        const answers = this.resumeAnswers();
        if (page && page !== this.currentPage) {
          this.currentPage = page;
          untracked(() => {
            this.activePage$.set(page);
            this.surveyViewerService.mapToQuestionControlsAndResume(page.questions, answers, { previewMode: this.previewMode() });
          });
        }
      },
      { allowSignalWrites: true }
    );

    effect(
      () => {
        const page = this.activePage$();
        if (
          page &&
          page.id !== this.automaticSubmissionPageId &&
          this.surveyViewerService.automaticQuestionIds$().size &&
          !this.visibleQuestions$().length &&
          !this.stopNextQuestionButton$()
        ) {
          this.automaticSubmissionPageId = page.id;
          untracked(() => this.next.emit());
        }
      },
      { allowSignalWrites: true }
    );
  }

  onNext(): void {
    this.controls$().forEach((ctrl) => ctrl.markAsTouched());
    if (!this.completionPending() && this.surveyViewerService.notAllCtrlsValid$()) {
      return;
    }

    this.next.emit();
  }

  onPrev(): void {
    if (this.stopBackButton$()) {
      return;
    }

    this.forceStop$.set(false);
    this.prev.emit();
  }
}
